import { getCollection } from './firestoreService';
import { getAnalyticsEvents } from './analyticsService';
import { getAllSubscriptions } from './subscriptionService';
import { convertTimestamp } from './firestoreService';
import { subDays, differenceInDays, format, startOfDay, startOfWeek, startOfMonth } from 'date-fns';

export interface CohortData {
  cohort: string;
  cohortDate: Date;
  totalUsers: number;
  retentionRates: {
    d1: number;
    d7: number;
    d30: number;
    d90: number;
  };
  revenue: number;
}

export interface RetentionMetrics {
  returnRate: number;
  churnRateByTier: Map<string, number>;
  reactivationRate: number;
  avgDaysBetweenSessions: number;
}

export interface RetentionAnalytics {
  cohorts: CohortData[];
  retentionMetrics: RetentionMetrics;
  cohortRetentionHeatmap: Array<{ cohort: string; period: string; retention: number }>;
}

export const getRetentionAnalytics = async (
  days: number = 90,
  cohortType: 'daily' | 'weekly' | 'monthly' = 'monthly'
): Promise<RetentionAnalytics> => {
  try {
    const rawUsers = await getCollection('users', undefined, 'createdAt', 'asc', 10000);
    const users = rawUsers.filter((u: any) => !u.deletedAt && !u.isDeleted);
    
    // Get all subscriptions for revenue calculation
    const subscriptions = await getAllSubscriptions();
    
    // Get analytics events for activity tracking
    const events = await getAnalyticsEvents(days * 2); // Get more events for retention calculation
    
    // Create cohorts based on signup dates
    const cohortMap = new Map<string, { users: any[]; signupDate: Date }>();
    
    users.forEach((user: any) => {
      const signupDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      let cohortKey: string;
      let cohortDate: Date;
      
      if (cohortType === 'daily') {
        cohortDate = startOfDay(signupDate);
        cohortKey = format(cohortDate, 'yyyy-MM-dd');
      } else if (cohortType === 'weekly') {
        cohortDate = startOfWeek(signupDate, { weekStartsOn: 1 });
        cohortKey = format(cohortDate, 'yyyy-MM-dd');
      } else {
        cohortDate = startOfMonth(signupDate);
        cohortKey = format(cohortDate, 'yyyy-MM');
      }
      
      if (!cohortMap.has(cohortKey)) {
        cohortMap.set(cohortKey, { users: [], signupDate: cohortDate });
      }
      cohortMap.get(cohortKey)!.users.push(user);
    });
    
    // Calculate retention for each cohort
    const cohorts: CohortData[] = [];
    const heatmapData: Array<{ cohort: string; period: string; retention: number }> = [];
    
    for (const [cohortKey, cohortData] of cohortMap.entries()) {
      const totalUsers = cohortData.users.length;
      const cohortDate = cohortData.signupDate;
      
      // Get user IDs in this cohort
      const cohortUserIds = new Set(cohortData.users.map((u: any) => u.id));
      
      // Calculate retention at different intervals
      const retentionRates = {
        d1: calculateRetentionRate(cohortUserIds, cohortDate, 1, events),
        d7: calculateRetentionRate(cohortUserIds, cohortDate, 7, events),
        d30: calculateRetentionRate(cohortUserIds, cohortDate, 30, events),
        d90: calculateRetentionRate(cohortUserIds, cohortDate, 90, events),
      };
      
      // Calculate revenue for this cohort
      const cohortRevenue = subscriptions
        .filter((sub: any) => cohortUserIds.has(sub.userId) && sub.status === 'active')
        .reduce((sum: number, sub: any) => sum + (sub.amount || 0), 0);
      
      cohorts.push({
        cohort: cohortKey,
        cohortDate,
        totalUsers,
        retentionRates,
        revenue: cohortRevenue,
      });
      
      // Add to heatmap data
      heatmapData.push(
        { cohort: cohortKey, period: 'D1', retention: retentionRates.d1 },
        { cohort: cohortKey, period: 'D7', retention: retentionRates.d7 },
        { cohort: cohortKey, period: 'D30', retention: retentionRates.d30 },
        { cohort: cohortKey, period: 'D90', retention: retentionRates.d90 }
      );
    }
    
    // Sort cohorts by date
    cohorts.sort((a, b) => a.cohortDate.getTime() - b.cohortDate.getTime());
    
    // Calculate overall retention metrics
    const retentionMetrics = calculateRetentionMetrics(users, events, subscriptions);
    
    return {
      cohorts,
      retentionMetrics,
      cohortRetentionHeatmap: heatmapData,
    };
  } catch (error) {
    console.error('Error calculating retention analytics:', error);
    throw error;
  }
};

function calculateRetentionRate(
  cohortUserIds: Set<string>,
  cohortDate: Date,
  daysAfter: number,
  events: any[]
): number {
  if (cohortUserIds.size === 0) return 0;
  
  const targetDate = new Date(cohortDate);
  targetDate.setDate(targetDate.getDate() + daysAfter);
  
  // Find users who were active within the retention period
  const activeUsers = new Set<string>();
  
  events.forEach((event: any) => {
    if (!cohortUserIds.has(event.userId)) return;
    
    const eventDate = event.timestamp instanceof Date ? event.timestamp : new Date(event.timestamp);
    const daysSinceSignup = differenceInDays(eventDate, cohortDate);
    
    // User is considered retained if they were active within the retention window
    if (daysSinceSignup >= 0 && daysSinceSignup <= daysAfter + 1) {
      // Also check if they were active around the target retention date (±3 days)
      const daysFromTarget = Math.abs(differenceInDays(eventDate, targetDate));
      if (daysFromTarget <= 3) {
        activeUsers.add(event.userId);
      }
    }
  });
  
  return (activeUsers.size / cohortUserIds.size) * 100;
}

function calculateRetentionMetrics(users: any[], events: any[], subscriptions: any[] = []): RetentionMetrics {
  // Calculate return rate (users who came back after first session)
  const usersWithFirstSession = new Set<string>();
  const usersWhoReturned = new Set<string>();
  
  events.forEach((event: any) => {
    if (event.eventName === 'session_start' || event.eventName === 'app_launch') {
      if (!usersWithFirstSession.has(event.userId)) {
        usersWithFirstSession.add(event.userId);
      } else {
        usersWhoReturned.add(event.userId);
      }
    }
  });
  
  const returnRate = usersWithFirstSession.size > 0
    ? (usersWhoReturned.size / usersWithFirstSession.size) * 100
    : 0;
  
  // Calculate churn rate by subscription tier
  const churnRateByTier = new Map<string, number>();
  const subscriptionsByTier = new Map<string, { total: number; canceled: number }>();
  
  subscriptions.forEach((sub: any) => {
    const tier = sub.planId || 'free';
    if (!subscriptionsByTier.has(tier)) {
      subscriptionsByTier.set(tier, { total: 0, canceled: 0 });
    }
    const tierData = subscriptionsByTier.get(tier)!;
    tierData.total++;
    if (sub.status === 'canceled' || sub.status === 'expired') {
      tierData.canceled++;
    }
  });
  
  subscriptionsByTier.forEach((data, tier) => {
    churnRateByTier.set(tier, data.total > 0 ? (data.canceled / data.total) * 100 : 0);
  });
  
  // Calculate reactivation rate (users who came back after 30+ days inactive)
  // This is a simplified calculation
  const reactivationRate = 0; // Would need more complex tracking
  
  // Calculate average days between sessions
  const sessionDates = new Map<string, Date[]>();
  events.forEach((event: any) => {
    if (event.eventName === 'session_start' || event.eventName === 'app_launch') {
      if (!sessionDates.has(event.userId)) {
        sessionDates.set(event.userId, []);
      }
      const eventDate = event.timestamp instanceof Date ? event.timestamp : new Date(event.timestamp);
      sessionDates.get(event.userId)!.push(eventDate);
    }
  });
  
  let totalDaysBetween = 0;
  let sessionCount = 0;
  
  sessionDates.forEach((dates) => {
    dates.sort((a, b) => a.getTime() - b.getTime());
    for (let i = 1; i < dates.length; i++) {
      const days = differenceInDays(dates[i], dates[i - 1]);
      totalDaysBetween += days;
      sessionCount++;
    }
  });
  
  const avgDaysBetweenSessions = sessionCount > 0 ? totalDaysBetween / sessionCount : 0;
  
  return {
    returnRate,
    churnRateByTier,
    reactivationRate,
    avgDaysBetweenSessions,
  };
}

